/**
 * Sitemap Page
 * Route: /sitemap
 * Features: Hierarchical view of all pages and resources in MemAds
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  Map,
  Tag,
  Briefcase,
  HelpCircle,
  ShieldCheck,
} from 'lucide-react';

const SitemapPage: React.FC = () => {
  const sections = [
    {
      title: 'Marketplace',
      icon: <Tag className="h-5 w-5 text-primary" />,
      links: [
        { name: 'Home / Main page', path: '/' },
        { name: 'Browse all listings', path: '/listings' },
        { name: 'Post a new advertisement', path: '/post' },
        { name: 'My Listings dashboard', path: '/my-listings' },
        { name: 'Seller Dashboard', path: '/seller-dashboard' },
        { name: 'Profile settings', path: '/settings' },
      ],
    },
    {
      title: 'Company',
      icon: <Briefcase className="h-5 w-5 text-primary" />,
      links: [
        { name: 'About MemAds', path: '/about' },
        { name: 'Careers & jobs', path: 'mailto:careers@memads.in' },
        { name: 'Latest news & Blog', path: '/blog' },
        { name: 'Press inquires', path: 'mailto:press@memads.in' },
      ],
    },
    {
      title: 'Support & Trust',
      icon: <HelpCircle className="h-5 w-5 text-primary" />,
      links: [
        { name: 'Help Center & FAQ', path: '/help' },
        { name: 'Safety Tips for trading', path: '/safety' },
        { name: 'Contact customer support', path: '/contact' },
      ],
    },
    {
      title: 'Legal policies',
      icon: <ShieldCheck className="h-5 w-5 text-primary" />,
      links: [
        { name: 'Terms of Service', path: '/terms' },
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Cookie Policy', path: '/cookies' },
        { name: 'Contact legal desk', path: 'mailto:legal@memads.in' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background" id="sitemap-page">
      <SEO
        title="Sitemap"
        description="Sitemap directory listing all pages, categories, and legal resource links on MemAds."
        canonical="/sitemap"
      />
      {/* Header */}
      <section className="relative bg-gradient-to-r from-slate-900 to-slate-800 py-14 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_60%,rgba(255,255,255,0.03),transparent_35%)]" />
        <div className="relative max-w-2xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <span className="grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white mx-auto mb-4">
            <Map className="h-6 w-6" />
          </span>
          <h1 className="font-heading text-3xl font-extrabold text-white">Sitemap</h1>
          <p className="text-sm text-white/80 mt-2">Find your way around MemAds portal easily.</p>
        </div>
      </section>

      {/* Grid of Sections */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid gap-8 md:grid-cols-2">
          {sections.map((section) => (
            <section key={section.title} className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground mb-4 pb-2 border-b border-border">
                {section.icon}
                {section.title}
              </h2>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.path.startsWith('/') ? (
                      <Link
                        to={link.path}
                        className="text-xs text-muted-foreground hover:text-primary hover:underline transition"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        href={link.path}
                        className="text-xs text-muted-foreground hover:text-primary hover:underline transition"
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
};

export default SitemapPage;
